<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { api, ApiError } from './api.js'
import { flushQueue } from './sync.js'
import AuthView from './components/AuthView.vue'
import TodayPage from './components/TodayPage.vue'
import StatsPage from './components/StatsPage.vue'
import MyPage from './components/MyPage.vue'

type Mode = 'boot' | 'auth' | 'app'
const mode = ref<Mode>('boot')
const initialized = ref(true)
const account = ref('')
const tab = ref<'today' | 'stats' | 'me'>('today')
const openedDate = ref<string>()
const initKey = ref('')
const offline = ref(!navigator.onLine)
const updateReady = ref(false)
let events: EventSource | null = null

const currentComponent = computed(() => tab.value === 'today' ? TodayPage : tab.value === 'stats' ? StatsPage : MyPage)

function connectEvents() {
  events?.close()
  events = new EventSource('/api/events')
  events.addEventListener('ledger', () => window.dispatchEvent(new CustomEvent('zaocan-remote-change')))
  events.onerror = () => { window.setTimeout(() => { if (mode.value === 'app') connectEvents() }, 2500) }
}

async function bootstrap() {
  const url = new URL(location.href)
  initKey.value = url.searchParams.get('init_token') ?? sessionStorage.getItem('zaocan-init-key') ?? ''
  if (initKey.value) { sessionStorage.setItem('zaocan-init-key', initKey.value); url.searchParams.delete('init_token'); history.replaceState(null, '', url) }
  try {
    const session = await api<{ account: string }>('/api/session')
    account.value = session.account
    localStorage.setItem('zaocan-account', session.account)
    mode.value = 'app'
    connectEvents()
    void flushQueue()
  } catch (reason) {
    const cachedAccount = localStorage.getItem('zaocan-account')
    if (cachedAccount && (!navigator.onLine || !(reason instanceof ApiError))) {
      account.value = cachedAccount
      mode.value = 'app'
      return
    }
    const status = await api<{ initialized: boolean }>('/api/setup/status')
    initialized.value = status.initialized
    mode.value = 'auth'
  }
}

function authenticated(name: string) {
  account.value = name
  localStorage.setItem('zaocan-account', name)
  mode.value = 'app'
  sessionStorage.removeItem('zaocan-init-key')
  connectEvents()
  void flushQueue()
}

function onlineChanged() { offline.value = !navigator.onLine; if (!offline.value) { void flushQueue(); window.dispatchEvent(new CustomEvent('zaocan-remote-change')) } }
function openDay(date: string) { openedDate.value = date; tab.value = 'today' }
function openToday() { openedDate.value = undefined; tab.value = 'today' }
onMounted(() => { void bootstrap(); addEventListener('online', onlineChanged); addEventListener('offline', onlineChanged); addEventListener('visibilitychange', onlineChanged); addEventListener('zaocan-update-ready', () => { updateReady.value = true }) })
onBeforeUnmount(() => { events?.close(); removeEventListener('online', onlineChanged); removeEventListener('offline', onlineChanged) })
</script>

<template>
  <div v-if="mode === 'boot'" class="boot"><div class="brand-mark">早</div><span>正在打开账本…</span></div>
  <AuthView v-else-if="mode === 'auth'" :initialized="initialized" :init-key="initKey" @authenticated="authenticated" />
  <div v-else class="app-shell">
    <div v-if="offline" class="network-banner">离线 · 显示缓存，修改会在联网后同步</div>
    <div v-if="updateReady" class="update-banner">新版本已就绪；待同步内容完成后可安全刷新。</div>
    <component :is="currentComponent" :key="`${tab}:${openedDate ?? ''}`" :account="account" :initial-date="openedDate" @open-day="openDay" />
    <nav class="bottom-nav" aria-label="主导航">
      <button type="button" :class="{ active: tab === 'today' }" :aria-current="tab === 'today' ? 'page' : undefined" @click="openToday">今日</button>
      <button type="button" :class="{ active: tab === 'stats' }" :aria-current="tab === 'stats' ? 'page' : undefined" @click="tab = 'stats'">统计</button>
      <button type="button" :class="{ active: tab === 'me' }" :aria-current="tab === 'me' ? 'page' : undefined" @click="tab = 'me'">我的</button>
    </nav>
  </div>
</template>
